import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";

import PrimaryButton from "../../Components/Buttons/primary-button.js";
import CustomRadioButton from "../../Components/Form Fields/radio-button.js";

import renderAddPayee from "../../Organisms/Modals/Pay Rent/add-payee.js";
import renderPayeeDetails from "../../Organisms/Modals/Pay Rent/payee-details.js";

import { useModalContext } from "../../Contexts/ModalContext.js";

import { useGetPayees } from "../../CustomHooks/Queries";

import { ArrowLeftCircle, AddCircleFilled, MultipleUsers, AddCircle } from "../../Assets/Icons";

import "./manage-payee.scss";

const PayeeCard = ({ payee, selectedPayee, selectable, onSelect }) => {
  const selectCardForPayment = () => {
    if (selectable) onSelect?.(payee);
  };

  const isSelected = payee?.counter_party_id === selectedPayee?.counter_party_id;

  return (
    <button
      className={`payee-card ${selectable ? "selectable" : "viewable"}${isSelected ? " selected" : ""}`}
      onClick={() => onSelect?.(payee)}
    >
      <div className="account-badge">{payee?.nameAcronym}</div>
      <div className="info-container">
        <p className="title">{payee?.name}</p>
        <p className="description">{payee?.account}</p>
      </div>
      <div className="actions">
        {selectable && <CustomRadioButton checked={isSelected} onChange={selectCardForPayment} />}
      </div>
    </button>
  );
};

export default function ManagePayeePage() {
  return (
    <div className="manage-payee">
      <section className="card-section-wrapper">
        <div className="max-width-container">
          <div className="card-section">
            <ManagePayee />
          </div>
        </div>
      </section>
    </div>
  );
}

export function ManagePayee({ presentedForSelection = false, preferredPayee, onChange }) {
  const [selectedPayee, setSelectedPayee] = useState(preferredPayee ?? null);

  const navigate = useNavigate();

  const modalContext = useModalContext();

  const { isLoading: isLoadingPayees, data: payees } = useGetPayees();

  const connectedPayees = useMemo(() => {
    const onPayeeSelect = (payee) => {
      if (presentedForSelection) {
        setSelectedPayee(payee);
      } else {
        renderPayeeDetails(modalContext, payee);
      }
    };

    return payees?.map((payee, idx) => (
      <PayeeCard
        key={idx}
        payee={payee}
        selectable={presentedForSelection}
        selectedPayee={selectedPayee}
        onSelect={onPayeeSelect}
      />
    ));
  }, [presentedForSelection, payees, modalContext, selectedPayee]);

  const handleConfirmChange = () => onChange(selectedPayee);

  const handleAddButtonClick = () => renderAddPayee(modalContext);

  return (
    <>
      {!presentedForSelection && (
        <div className="section-header">
          <button className="go-back-button" onClick={() => navigate(-1)} aria-label="Go back">
            <ArrowLeftCircle />
          </button>
          <h3 className="section-title">Manage Payees</h3>
          {!presentedForSelection && (
            <button className="add-button" onClick={handleAddButtonClick} aria-label="Add payee">
              <AddCircle />
            </button>
          )}
        </div>
      )}
      {isLoadingPayees ? (
        <div className="loading-content">
          <CircularProgress />
        </div>
      ) : connectedPayees?.length > 0 ? (
        <div className="content mt-3">
          <div className="top-container">
            {connectedPayees}
            {presentedForSelection && (
              <button className="add-payee" onClick={handleAddButtonClick}>
                <AddCircleFilled />
                Add New Payee
              </button>
            )}
          </div>
          {presentedForSelection && (
            <div className="bottom-container stick-to-modal-bottom">
              <PrimaryButton onClick={handleConfirmChange}>Confirm</PrimaryButton>
            </div>
          )}
        </div>
      ) : (
        <div className={`no-payee-found${presentedForSelection ? " mx-sm-6 mx-4" : ""}`}>
          <div className="users-icon mb-2">
            <MultipleUsers />
          </div>
          <p className="no-payee-message mb-sm-6 mb-4">
            You haven't added any payees yet. Add a payee to start sending rent payments.
          </p>
          <PrimaryButton onClick={handleAddButtonClick}>Add New Payee</PrimaryButton>
        </div>
      )}
    </>
  );
}
